/**
 * @typedef {object} OrderResponse
 * @property {object} order
 * @property {AutoLineRedemptionsResult} autoLineRedemptionsResult
 */

/**
 * @typedef {object} AutoLineRedemptionsResult
 * @property {Object<number, ActionResult>} lines
 */

/**
 * @typedef {object} ActionResult
 * @property {boolean} success
 * @property {number} count
 * @property {string} [error]
 * @property {string} [errorType]
 */

const ApiError = require('@/lib/Api/ApiError');
const ApiResolvers = require('@/lib/Api/ApiResolvers');
const Order = require('@/lib/Orders/Order');
const { formatDateForApi } = require('@/lib/Dates/dateFunctions');
const { hydrate } = require('@/lib/Hydration');

/**
 * Wraps some methods from the orderResource into promises.
 */
class OrdersApi {
    /**
     * @param {ApiClient} apiClient
     */
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    /**
     * This is an order for the old style / offline structure which includes payments.
     * Hence, we need to add confirmed, confirmedAt, and requestAutoRedeem.
     *
     * @param {OrderConfirmationStructure} orderConfirmationStructure
     *
     * @returns {Promise<OrderResponse>}
     * @throws {ApiError}
     */
    async createConfirmedOrder(orderConfirmationStructure) {
        return this.apiClient
            .request(
                'POST',
                OrdersApi.getCreateOrderEndpoint(),
                {
                    ...orderConfirmationStructure,

                    // This is the default value, but we're being explicit.
                    confirmed: 1,

                    // confirmedAt/paidAt is no longer part of the confirmation structure.
                    confirmedAt: formatDateForApi(new Date()),

                    // As we are confirming the order now we want auto redemption to happen.
                    requestAutoRedeem: 1,
                },
            )
            .then(({ data }) => {
                this.checkForErrorsInResponse(data);
                this.checkForOrderInResponse(data);
                return data;
            });
    }

    /**
     * This is an order for the new style flow where we create an unconfirmed order, take payment, then confirm.
     *
     * @param {OrderConfirmationStructure} orderConfirmationStructure
     *
     * @returns {Promise<OrderResponse>}
     * @throws {ApiError}
     */
    async createUnconfirmedOrder(orderConfirmationStructure) {
        return this.apiClient
            .request(
                'POST',
                OrdersApi.getCreateOrderEndpoint(),
                {
                    ...orderConfirmationStructure,

                    // Create an unconfirmed order.
                    confirmed: 0,

                    // requestAutoRedeem is not sent because we're not confirming. That's sent in the confirm request.
                }
            )
            .then(({ data }) => {
                this.checkForErrorsInResponse(data);
                this.checkForOrderInResponse(data);
                return data;
            });
    }

    /**
     * Confirm an unconfirmed order created with the new style order flow.
     *
     * @param {number} orderID
     *
     * @returns {Promise<OrderResponse>}
     * @throws {ApiError}
     */
    async confirmOrder(orderID) {
        return this.apiClient
            .request(
                'POST',
                `orders/${orderID}/confirm`,
                {
                    // We don't need to provide confirmedAt - the API will generate this.

                    // Request automatic redemption now that the order is confirmed.
                    requestAutoRedeem: 1,
                }
            )
            .then(({ data }) => data);
    }

    /**
     * @param {number} orderID
     *
     * @returns {Promise}
     */
    async cancelOrder(orderID) {
        return this.apiClient
            .request(
                'DELETE',
                `orders/${orderID}`
            )
            .then(({ data }) => data);
    }

    /**
     * Make a dry run order request for the old style order flow.
     *
     * @param {OrderConfirmationStructure} orderConfirmationStructure
     *
     * @returns {Promise<OrderResponse>}
     * @throws {ApiError}
     */
    async dryRunCreateOrder(orderConfirmationStructure) {
        return this.apiClient
            .request(
                'POST',
                'orders',
                {
                    ...orderConfirmationStructure,

                    // Perform a dry-run to validate the order. Don't actually create it.
                    dryRun: 1,
                },
            )
            .then(({ data }) => {
                this.checkForErrorsInResponse(data);
                // We don't need to check for the order in the response because it's a dry run.
                return data;
            });
    }

    /**
     * @private
     * @param {OrderResponse} response
     * @throws {ApiError}
     */
    checkForErrorsInResponse(response) {
        // Check for empty response.
        if (!response) {
            throw new ApiError(
                'An empty response was received for the order request.',
                200
            );
        }

        // Check if the response says there was an error.
        if (response.error) {
            throw new ApiError(
                response.message || response.error || 'The order request failed.',
                200,
                response
            );
        }

        // Check if the response does not say there was a success.
        if (!response.success) {
            throw new ApiError(
                response.message || response.error || 'The order request failed.',
                200,
                response
            );
        }
    }

    /**
     * @private
     * @param {OrderResponse} response
     * @throws {ApiError}
     */
    checkForOrderInResponse(response) {
        // Ensure the order is in the response.
        if (!response.order) {
            throw new ApiError(
                'The server indicated the order was created successfully, but did not return the order.',
                200,
                response
            );
        }
    }

    /**
     * @param {string} thirdPartyRef
     *
     * @returns {Promise<Order>}
     */
    async findByThirdPartyRef(thirdPartyRef) {
        return this.apiClient
            .request(
                'GET',
                `orders/by-third-party-ref?thirdPartyRef=${thirdPartyRef}`,
            )
            .then(({ data }) => {
                // Found a saved order with this ref. Create an Order object from it.
                // Note that while the endpoint does support it, this request does not ask for any resolvers. So a lot
                // of expected stuff may be missing such as payments and items.
                // Only the bookingRef and ID are guaranteed and useful from here.
                const order = new Order();
                hydrate(data, order);
                return order;
            });
    }

    static getCreateOrderEndpoint() {
        return 'orders?'
            + 'resolve=' + [
            ApiResolvers.ORDER_DOCUMENT_NUMBERS,
        ].join(',');
    }
}

module.exports = OrdersApi;
